/**
 * useCalendar Hook
 * 
 * Custom hook for managing calendar state, navigation, and event operations.
 * Phase 1: Basic state management with local state
 * Phase 2+: Integration with Redux and API
 */

import { useReducer, useCallback } from 'react';
import type { CalendarState, CalendarAction, CalendarView, CalendarEvent, EventCategory } from '../types/calendar';
import { getNextDate, getPreviousDate } from '../utils/calendarHelpers';

// Initial state
const initialState: CalendarState = {
  currentDate: new Date(),
  currentView: 'month',
  events: [],
  categories: [],
  selectedEvent: undefined,
  expandedDates: new Set<string>(),
  isLoading: false,
  error: undefined,
};

// Reducer function
const calendarReducer = (state: CalendarState, action: CalendarAction): CalendarState => {
  switch (action.type) {
    case 'SET_DATE':
      return {
        ...state,
        currentDate: action.payload,
      };
      
    case 'SET_VIEW':
      return {
        ...state,
        currentView: action.payload,
      };
      
    case 'SET_EVENTS':
      return {
        ...state,
        events: action.payload,
        isLoading: false,
        error: undefined,
      };
      
    case 'SET_CATEGORIES':
      return {
        ...state,
        categories: action.payload,
      };
      
    case 'SELECT_EVENT':
      return {
        ...state,
        selectedEvent: action.payload,
      };
      
    case 'TOGGLE_DATE_EXPANSION': {
      const newExpanded = new Set(state.expandedDates);
      if (newExpanded.has(action.payload)) {
        newExpanded.delete(action.payload);
      } else {
        newExpanded.add(action.payload);
      }
      return {
        ...state,
        expandedDates: newExpanded,
      };
    }
      
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
      
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };
      
    case 'NAVIGATE_NEXT':
      return {
        ...state,
        currentDate: getNextDate(state.currentDate, state.currentView),
      };
      
    case 'NAVIGATE_PREVIOUS':
      return {
        ...state,
        currentDate: getPreviousDate(state.currentDate, state.currentView),
      };
      
    case 'NAVIGATE_TODAY':
      return {
        ...state,
        currentDate: new Date(),
      };
      
    default:
      return state;
  }
};

/**
 * useCalendar Hook
 * 
 * Manages calendar state and provides actions for navigation and event management
 */
export const useCalendar = (initialView: CalendarView = 'month') => {
  const [state, dispatch] = useReducer(calendarReducer, {
    ...initialState,
    currentView: initialView,
  });

  // Navigation actions
  const navigateNext = useCallback(() => {
    dispatch({ type: 'NAVIGATE_NEXT' });
  }, []);

  const navigatePrevious = useCallback(() => {
    dispatch({ type: 'NAVIGATE_PREVIOUS' });
  }, []);

  const navigateToday = useCallback(() => {
    dispatch({ type: 'NAVIGATE_TODAY' });
  }, []);

  const setDate = useCallback((date: Date) => {
    dispatch({ type: 'SET_DATE', payload: date });
  }, []);

  // View actions
  const setView = useCallback((view: CalendarView) => {
    dispatch({ type: 'SET_VIEW', payload: view });
  }, []);

  // Event actions
  const setEvents = useCallback((events: CalendarEvent[]) => {
    dispatch({ type: 'SET_EVENTS', payload: events });
  }, []);

  const selectEvent = useCallback((event: CalendarEvent | undefined) => {
    dispatch({ type: 'SELECT_EVENT', payload: event });
  }, []);

  const toggleDateExpansion = useCallback((dateKey: string) => {
    dispatch({ type: 'TOGGLE_DATE_EXPANSION', payload: dateKey });
  }, []);

  // Category actions
  const setCategories = useCallback((categories: EventCategory[]) => {
    dispatch({ type: 'SET_CATEGORIES', payload: categories });
  }, []);

  // Loading and error actions
  const setLoading = useCallback((isLoading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: isLoading });
  }, []);

  const setError = useCallback((error: string | undefined) => {
    dispatch({ type: 'SET_ERROR', payload: error });
  }, []);

  // Computed values
  const isDateExpanded = useCallback((dateKey: string) => {
    return state.expandedDates.has(dateKey);
  }, [state.expandedDates]);

  return {
    // State
    currentDate: state.currentDate,
    currentView: state.currentView,
    events: state.events,
    categories: state.categories,
    selectedEvent: state.selectedEvent,
    isLoading: state.isLoading,
    error: state.error,
    
    // Navigation
    navigateNext,
    navigatePrevious,
    navigateToday,
    setDate,
    
    // View
    setView,
    
    // Events
    setEvents,
    selectEvent,
    
    // Categories
    setCategories,
    
    // Date expansion
    toggleDateExpansion,
    isDateExpanded,
    
    // Loading & Errors
    setLoading,
    setError,
  };
};

/**
 * useCalendarEvents Hook
 * 
 * Hook for managing event operations (Phase 2+: API integration)
 * Phase 1: Returns mock data
 */
export const useCalendarEvents = () => {
  // Phase 1: Mock implementation
  // Phase 2+: API integration with RTK Query
  
  const fetchEvents = useCallback(async (startDate: Date, endDate: Date) => {
    // TODO: Phase 2 - Implement API call
    console.log('Fetching events from', startDate, 'to', endDate);
    return [];
  }, []);

  const createEvent = useCallback(async (eventData: Partial<CalendarEvent>) => {
    // TODO: Phase 2 - Implement API call
    console.log('Creating event:', eventData);
    return null;
  }, []);

  const updateEvent = useCallback(async (eventId: string, eventData: Partial<CalendarEvent>) => {
    // TODO: Phase 2 - Implement API call
    console.log('Updating event:', eventId, eventData);
    return null;
  }, []);

  const deleteEvent = useCallback(async (eventId: string) => {
    // TODO: Phase 2 - Implement API call
    console.log('Deleting event:', eventId);
    return null;
  }, []);

  const lockEvent = useCallback(async (eventId: string, reason: string) => {
    // TODO: Phase 2 - Implement API call
    console.log('Locking event:', eventId, 'Reason:', reason);
    return null;
  }, []);

  const unlockEvent = useCallback(async (eventId: string) => {
    // TODO: Phase 2 - Implement API call
    console.log('Unlocking event:', eventId);
    return null;
  }, []);

  return {
    fetchEvents,
    createEvent,
    updateEvent,
    deleteEvent,
    lockEvent,
    unlockEvent,
  };
};

/**
 * useCalendarCategories Hook
 * 
 * Hook for managing event categories (Phase 2+: API integration)
 * Phase 1: Returns mock categories
 */
export const useCalendarCategories = () => {
  const fetchCategories = useCallback(async () => {
    // TODO: Phase 2 - Implement API call
    // For now, return mock categories
    const mockCategories: EventCategory[] = [
      {
        id: '1',
        name: 'Delivery',
        slug: 'delivery',
        color: 'blue',
        icon: 'truck',
        description: 'Delivery and shipping events',
        is_active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
      {
        id: '2',
        name: 'Production',
        slug: 'production',
        color: 'green',
        icon: 'factory',
        description: 'Production milestones and deadlines',
        is_active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
      {
        id: '3',
        name: 'Pending',
        slug: 'pending',
        color: 'amber',
        icon: 'clock',
        description: 'Pending tasks and approvals',
        is_active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
    ];
    
    return mockCategories;
  }, []);

  return {
    fetchCategories,
  };
};

